<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :closable="true"
    :style="{ width: dialogWidth }"
    header="Управление отчётами"
    @hide="onClose"
  >
    <div class="reports-modal">
      <!-- Вкладки -->
      <TabView v-model:activeIndex="activeTab">
        <!-- Вкладка 1: Создать отчёт -->
        <TabPanel header="Создать отчёт">
          <!-- Выбор типа отчета -->
          <div class="report-types">
            <Card
              class="report-card"
              :class="{ selected: selectedType === 'price', disabled: hasActiveReport }"
              @click="selectReport('price')"
            >
              <template #title>
                <div class="report-card-title">
                  <i class="pi pi-chart-bar"></i>
                  <span>Отчет по прайс-листу</span>
                  <Tag v-if="hasActiveReport" severity="warning" value="В процессе" class="ml-2"></Tag>
                </div>
              </template>
              <template #content>
                <p class="report-description">
                  Анализ наличия фотографий и карточек товаров в ProTrade.
                  Детальная аналитика по брендам и доступности.
                </p>
                <div v-if="hasActiveReport" class="active-report-info">
                  <Message severity="info" :closable="false">
                    Отчёт уже генерируется. Перейдите во вкладку "История" чтобы увидеть прогресс.
                  </Message>
                </div>
              </template>
            </Card>

            <!-- Здесь в будущем можно добавить другие типы отчетов -->
          </div>
        </TabPanel>

        <!-- Вкладка 2: История отчётов -->
        <TabPanel header="История отчётов">
          <div class="reports-history">
            <!-- Кнопка обновления -->
            <div class="history-toolbar">
              <Button
                label="Обновить"
                icon="pi pi-refresh"
                @click="loadReports"
                :loading="loadingReports"
                class="p-button-sm p-button-text"
              />
            </div>

            <!-- Таблица отчётов -->
            <DataTable
              :value="reports"
              :loading="loadingReports"
              responsiveLayout="scroll"
              stripedRows
              :paginator="reports.length > 10"
              :rows="10"
              class="reports-table"
            >
              <Column field="id" header="ID" :sortable="true" style="width: 80px"></Column>
              
              <Column field="status" header="Статус" :sortable="true" style="width: 150px">
                <template #body="slotProps">
                  <Tag
                    :value="getStatusLabel(slotProps.data.status)"
                    :severity="getStatusSeverity(slotProps.data.status)"
                  ></Tag>
                </template>
              </Column>

              <Column field="progress" header="Прогресс" style="width: 200px">
                <template #body="slotProps">
                  <ProgressBar
                    v-if="slotProps.data.status === 'processing'"
                    :value="slotProps.data.progress"
                    :showValue="true"
                  ></ProgressBar>
                  <span v-else-if="slotProps.data.status === 'completed'">100%</span>
                  <span v-else>—</span>
                </template>
              </Column>

              <Column field="created_at" header="Создан" :sortable="true">
                <template #body="slotProps">
                  {{ formatDate(slotProps.data.created_at) }}
                </template>
              </Column>

              <Column field="completed_at" header="Завершён">
                <template #body="slotProps">
                  {{ slotProps.data.completed_at ? formatDate(slotProps.data.completed_at) : '—' }}
                </template>
              </Column>

              <Column header="Действия" style="width: 200px">
                <template #body="slotProps">
                  <div class="action-buttons">
                    <Button
                      v-if="slotProps.data.status === 'completed'"
                      icon="pi pi-download"
                      class="p-button-sm p-button-success"
                      v-tooltip.top="'Скачать'"
                      @click="downloadReportById(slotProps.data.id)"
                    />
                    <Button
                      icon="pi pi-trash"
                      class="p-button-sm p-button-danger"
                      v-tooltip.top="'Удалить'"
                      @click="confirmDelete(slotProps.data)"
                      :disabled="slotProps.data.status === 'processing'"
                    />
                  </div>
                </template>
              </Column>

              <template #empty>
                <div class="empty-state">
                  <i class="pi pi-inbox" style="font-size: 3rem; color: var(--text-color-secondary);"></i>
                  <p>Нет отчётов</p>
                </div>
              </template>
            </DataTable>
          </div>
        </TabPanel>
      </TabView>
    </div>

    <template #footer>
      <Button
        label="Закрыть"
        icon="pi pi-times"
        @click="close"
        class="p-button-text"
      />
      <Button
        v-if="activeTab === 0"
        label="Начать генерацию"
        icon="pi pi-play"
        @click="startGeneration"
        :disabled="!selectedType || hasActiveReport"
        class="p-button-success"
      />
    </template>
  </Dialog>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import Dialog from 'primevue/dialog'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Tag from 'primevue/tag'
import Card from 'primevue/card'
import Button from 'primevue/button'
import ProgressBar from 'primevue/progressbar'
import Message from 'primevue/message'
import Skeleton from 'primevue/skeleton'
import Divider from 'primevue/divider'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import reportService from '@/services/reportService'

export default {
  name: 'ReportsModal',
  components: {
    Dialog,
    TabView,
    TabPanel,
    DataTable,
    Column,
    Tag,
    Card,
    Button,
    ProgressBar,
    Message,
    Skeleton,
    Divider
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:visible'],
  setup(props, { emit }) {
    const toast = useToast()
    const confirm = useConfirm()

    // Состояние модального окна
    const isVisible = ref(props.visible)
    const selectedType = ref('price')
    const activeTab = ref(0)

    // Состояние генерации
    const currentReportId = ref(null)
    const pollingInterval = ref(null)

    // История отчётов
    const reports = ref([])
    const loadingReports = ref(false)
    const hasActiveReport = ref(false)

    // Адаптивная ширина диалога
    const dialogWidth = computed(() => {
      if (window.innerWidth < 768) return '90vw'
      return '70vw'
    })

    // Синхронизация с пропсом
    watch(() => props.visible, (newVal) => {
      isVisible.value = newVal
    })

    watch(isVisible, (newVal) => {
      emit('update:visible', newVal)
    })

    // Выбор типа отчета
    const selectReport = (type) => {
      if (hasActiveReport.value) {
        toast.add({
          severity: 'warn',
          summary: 'Отчёт уже генерируется',
          detail: 'Дождитесь завершения текущего отчёта',
          life: 3000
        })
        return
      }
      selectedType.value = type
    }

    // Загрузка списка отчётов
    const loadReports = async () => {
      try {
        loadingReports.value = true
        // Фильтруем только отчеты по прайс-листу (price_list_analysis)
        const reportsList = await reportService.listReports({ 
          limit: 50,
          report_type: 'price_list_analysis'  // Только отчеты по прайс-листу
        })
        reports.value = reportsList

        // Проверяем есть ли активный отчёт
        const hasActive = reportsList.some(
          r => r.report_type === 'price_list_analysis' && 
               (r.status === 'pending' || r.status === 'processing')
        )
        
        hasActiveReport.value = hasActive
        
        // Управляем polling на основе активных отчётов
        if (hasActive && !pollingInterval.value) {
          startPolling()
        } else if (!hasActive && pollingInterval.value) {
          stopPolling()
        }
      } catch (error) {
        console.error('Error loading reports:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось загрузить список отчётов',
          life: 3000
        })
      } finally {
        loadingReports.value = false
      }
    }

    // Polling для автообновления списка отчётов
    const startPolling = () => {
      if (pollingInterval.value) return // Уже запущен
      
      pollingInterval.value = setInterval(async () => {
        await loadReports()
      }, 3000) // Проверяем каждые 3 секунды
    }

    const stopPolling = () => {
      if (pollingInterval.value) {
        clearInterval(pollingInterval.value)
        pollingInterval.value = null
      }
    }

    // Запуск генерации
    const startGeneration = async () => {
      if (hasActiveReport.value) {
        toast.add({
          severity: 'warn',
          summary: 'Отчёт уже генерируется',
          detail: 'Дождитесь завершения текущего отчёта',
          life: 3000
        })
        return
      }

      try {
        // Запускаем генерацию
        const report = await reportService.generateReport()
        currentReportId.value = report.id
        hasActiveReport.value = true

        // Переключаемся на вкладку истории
        activeTab.value = 1

        // Обновляем список отчётов
        await loadReports()

        toast.add({
          severity: 'info',
          summary: 'Генерация запущена',
          detail: 'Отчёт запущен. Отслеживайте прогресс во вкладке "История отчётов"',
          life: 5000
        })
      } catch (error) {
        console.error('Error starting generation:', error)
        
        let errorDetail = 'Не удалось запустить генерацию отчета. Проверьте подключение к серверу.'
        
        if (error.response && error.response.status === 429) {
          errorDetail = error.response.data.detail
        } else if (error.response && error.response.status === 409) {
          errorDetail = error.response.data.detail
          hasActiveReport.value = true
        }

        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: errorDetail,
          life: 5000
        })
      }
    }
    // Скачивание отчета по ID из истории
    const downloadReportById = async (reportId) => {
      try {
        await reportService.downloadReport(reportId)

        toast.add({
          severity: 'success',
          summary: 'Файл скачан',
          detail: 'Excel отчет успешно скачан',
          life: 3000
        })
      } catch (error) {
        console.error('Error downloading report:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось скачать отчет',
          life: 5000
        })
      }
    }

    // Подтверждение удаления
    const confirmDelete = (report) => {
      confirm.require({
        message: `Вы уверены что хотите удалить отчёт #${report.id}?`,
        header: 'Подтверждение удаления',
        icon: 'pi pi-exclamation-triangle',
        accept: () => deleteReport(report.id),
        acceptLabel: 'Удалить',
        rejectLabel: 'Отмена',
        acceptClass: 'p-button-danger'
      })
    }

    // Удаление отчёта
    const deleteReport = async (reportId) => {
      try {
        await reportService.deleteReport(reportId)
        
        toast.add({
          severity: 'success',
          summary: 'Удалено',
          detail: 'Отчёт успешно удалён',
          life: 3000
        })

        // Обновляем список
        await loadReports()
      } catch (error) {
        console.error('Error deleting report:', error)
        toast.add({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось удалить отчёт',
          life: 3000
        })
      }
    }

    // Форматирование даты
    const formatDate = (dateString) => {
      if (!dateString) return '—'
      const date = new Date(dateString)
      return new Intl.DateTimeFormat('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
    }

    // Метки статусов
    const getStatusLabel = (status) => {
      const labels = {
        pending: 'Ожидание',
        processing: 'В процессе',
        completed: 'Готов',
        failed: 'Ошибка',
        cancelled: 'Отменён'
      }
      return labels[status] || status
    }

    const getStatusSeverity = (status) => {
      const severities = {
        pending: 'info',
        processing: 'warning',
        completed: 'success',
        failed: 'danger',
        cancelled: 'secondary'
      }
      return severities[status] || 'info'
    }

    // Закрытие модального окна
    const close = () => {
      isVisible.value = false
    }

    const onClose = () => {
      close()
    }

    // Загрузка отчётов при открытии модального окна
    watch(() => props.visible, async (newVal) => {
      if (newVal) {
        await loadReports()
      }
    })

    // Загрузка отчётов при монтировании
    onMounted(async () => {
      if (props.visible) {
        await loadReports()
      }
    })

    // Очистка polling при размонтировании
    onUnmounted(() => {
      stopPolling()
    })

    return {
      isVisible,
      selectedType,
      activeTab,
      dialogWidth,
      reports,
      loadingReports,
      hasActiveReport,
      selectReport,
      startGeneration,
      loadReports,
      downloadReportById,
      confirmDelete,
      formatDate,
      getStatusLabel,
      getStatusSeverity,
      close,
      onClose
    }
  }
}
</script>

<style scoped>
.reports-modal {
  min-height: 300px;
}

.report-types {
  display: grid;
  gap: 1rem;
  margin-bottom: 1rem;
}

.report-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.report-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.report-card.selected {
  border-color: var(--primary-color);
  background-color: rgba(var(--primary-color-rgb), 0.05);
}

.report-card-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.2rem;
}

.report-card-title i {
  color: var(--primary-color);
}

.report-description {
  color: var(--text-color-secondary);
  margin: 0;
}

/* Стили для истории отчётов */
.reports-history {
  min-height: 400px;
}

.history-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
}

.reports-table {
  margin-top: 1rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-color-secondary);
}

.empty-state p {
  margin-top: 1rem;
  font-size: 1.1rem;
}

.report-card.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.report-card.disabled:hover {
  transform: none;
  box-shadow: none;
}

.active-report-info {
  margin-top: 1rem;
}

/* Мобильная адаптация */
@media (max-width: 768px) {
  .action-buttons {
    flex-wrap: wrap;
  }
}
</style>
